import { useEffect, useState } from 'react';

/**
 * Three states, not two. "System" is the default and follows the operating
 * system; the other two are an explicit override that outlives the session.
 * Without the third state there is no way back to "whatever the machine says"
 * once someone has touched the switch.
 */
export type ThemePreference = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'sdg.theme';
const ORDER: ThemePreference[] = ['system', 'light', 'dark'];

export function readThemePreference(): ThemePreference {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    // Private mode and blocked site data both throw here. The default is fine.
  }
  return 'system';
}

/**
 * "system" removes the attribute rather than resolving the preference itself,
 * so the media query in the stylesheet stays in charge and a machine that
 * switches at sunset switches this along with it, without a reload.
 */
export function applyThemePreference(preference: ThemePreference): void {
  const root = document.documentElement;
  if (preference === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', preference);

  try {
    if (preference === 'system') window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    // Not being able to remember the choice is not a reason to refuse it.
  }
}

export function nextThemePreference(current: ThemePreference): ThemePreference {
  return ORDER[(ORDER.indexOf(current) + 1) % ORDER.length];
}

export function useThemePreference(): [ThemePreference, (next: ThemePreference) => void] {
  const [preference, setPreference] = useState<ThemePreference>(readThemePreference);

  useEffect(() => {
    applyThemePreference(preference);
  }, [preference]);

  return [preference, setPreference];
}
